import React, { memo, Dispatch, SetStateAction, useEffect } from 'react';
import { Typography } from '@surveycake/rc';
import { SurveyPreviewAnswerWithValidation } from '@surveycake/logic-engine';
import { useEngineInstance } from '@surveycake/react-logic-engine';
import { Button, Modal } from 'app/shared/components';
import { useSurveyConfig } from 'app/core/survey-config';
import { scrollToSubject } from 'app/modules/subject';
import { isInIframe } from 'config/system';
import PostAnswer from '../../PostAnswer';
import { useStyles } from './styles';

export interface PreviewModalProps {
  open: boolean;
  data: Omit<SurveyPreviewAnswerWithValidation, 'validation'>;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function PreviewModal({ open, data, setOpen }: PreviewModalProps) {
  const engine = useEngineInstance();
  const { wording } = useSurveyConfig();
  const styles = useStyles([data.editable]);

  useEffect(() => {
    if (open && isInIframe) {
      window.parent.postMessage({ type: 'OPEN_PREVIEW_MODAL' }, '*');
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <Modal
      className={styles.previewModal}
      onOpen={() => {
        document.body.style.overflowY = 'hidden';
      }}
      onClose={() => {
        document.body.style.overflowY = '';
      }}
    >
      <header>
        <Typography variant="h4" color="text.B6" gutterBottom>
          {wording.common.preview_answer.title}
        </Typography>
        <Typography variant="h6" color="text.B4">
          {wording.common.preview_answer.subtitle}
        </Typography>
      </header>
      <div>
        {data.preview.map(item => (
          <div
            key={item.id}
            className={styles.previewItem}
            onClick={() => {
              if (data.editable) {
                setOpen(false);
                engine.goPage(item.page);
                setTimeout(() => scrollToSubject(item.id), 250);
              }
            }}
          >
            <Typography variant="h6" color="text.B6">
              {item.text}
            </Typography>
            <div className={styles.previewItemAnswerContainer}>
              {item.answer && (
                <Typography variant="div" color="text.B5">
                  {item.answer}
                </Typography>
              )}
            </div>
          </div>
        ))}
      </div>
      <footer>
        <Button className={styles.action} onClick={() => setOpen(false)}>
          {wording.common.preview_answer.action.cancel}
        </Button>
        <PostAnswer
          className={styles.action}
          label={wording.common.preview_answer.action.confirm}
        />
      </footer>
    </Modal>
  );
}

export default memo(PreviewModal);
